import React, { useMemo, useState } from 'react';
import Drawer from 'js/components/drawer/drawer';
import { RuleSet } from 'js/utils';
import ruleItemTypes from 'js/enums/rule-item-types.enum';
import Box from 'js/components/box/box';
import Panel from 'js/components/panel/panel';
import ruleTypes from 'js/enums/rule-types.enum';
import SectionTitle from 'js/components/section-title/section-title';
import TopicSelector from 'js/components/topic-selector/topic-selector';
import Text from 'js/components/text/text';

function groupTopics(topics, itemType) {
    const groups = {};
    const sortedTopics = [...topics].sort((a, b) =>
        a.name.localeCompare(b.name),
    );
    sortedTopics.forEach((topic) => {
        const topicId = String(topic.id);
        if (!groups[topic.group]) {
            groups[topic.group] = {
                id: String(topic.group),
                name: topic.group_name,
                logo: topic.group_logo,
            };
            groups[topic.group][itemType] = [];
        }
        groups[topic.group][itemType].push({ ...topic, id: topicId });
    });
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
}

export default function TopicsLibraryDrawer({
    topics = [],
    form = { topics: [] },
    onChange,
    onClose,
}) {
    const topicGroups = groupTopics(topics, ruleItemTypes.TOPIC);
    const [selectedTopics, setSelectedTopics] = useState(form.topics || []);
    const validFormTopics = selectedTopics.filter(
        (t) => t && t.id !== undefined,
    );
    const mockRules = useMemo(
        () =>
            new RuleSet([
                {
                    key: 0,
                    aggregation: ruleTypes.INCLUDED,
                    keywords: [],
                    topics: validFormTopics.map((t) => String(t.id)),
                },
            ]),
        [validFormTopics],
    );

    const toggleTopic = (topicId) => {
        const found = topics.find((t) => String(t.id) === String(topicId));
        if (!found) {
            return;
        }

        const isSelected = selectedTopics.some(
            (t) => String(t.id) === String(topicId),
        );
        const updatedTopics = isSelected
            ? selectedTopics.filter((t) => String(t.id) !== String(topicId))
            : [...selectedTopics, found];
        const sortedTopics = [...updatedTopics].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
        setSelectedTopics(sortedTopics);
        onChange(sortedTopics);
    };

    const toggleTopicGroup = (groupId) => {
        const group = topicGroups.find((g) => String(g.id) === String(groupId));
        if (!group) return;

        const updatedTopics = [
            ...selectedTopics,
            ...group.topics.filter(
                (topic) =>
                    !selectedTopics.some(
                        (t) => String(t.id) === String(topic.id),
                    ),
            ),
        ];
        const sortedTopics = [...updatedTopics].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
        setSelectedTopics(sortedTopics);
        onChange(sortedTopics);
    };

    return (
        <Drawer onClose={onClose} title="Full Topics Library">
            <Box padding="large">
                <SectionTitle hasBar>
                    <span style={{ fontWeight: 'normal' }}>Topic Library </span>
                </SectionTitle>

                <Box margin={['small', 0, 'large']}>
                    <Text size="small" color={['gray', 'darkest']}>
                        Topics inform where your advertising will appear. Browse
                        or search the taxonomy to refine your targeting.
                    </Text>
                </Box>

                <Panel bordered>
                    <Box padding={['small', 'base']} margin={['small', 0, 0]}>
                        <Text weight="bold" size="base">
                            Target content featuring
                        </Text>

                        <Box margin={['small', 0, 'small']}>
                            <TopicSelector
                                itemDisplayName="topics"
                                itemType={ruleItemTypes.TOPIC}
                                groups={topicGroups}
                                rules={mockRules.rules}
                                ruleKey={0}
                                onTarget={(topicId) => {
                                    toggleTopic(topicId);
                                }}
                                onTargetAll={(groupId) =>
                                    toggleTopicGroup(groupId)
                                }
                                placeholder="Search topics"
                                onDone={() => {
                                    onChange(selectedTopics);
                                    onClose();
                                }}
                            />
                        </Box>
                    </Box>
                </Panel>
            </Box>
        </Drawer>
    );
}
